import { useEffect, useRef, useState } from 'react';
import { savePosterCopy, type PosterCopyField } from './mockCopySave';

export function usePosterCopy(field: PosterCopyField, initialValue: string) {
  const [value, setValue] = useState(initialValue);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const draft = useRef(initialValue);
  const saved = useRef(initialValue);
  const pending = useRef<{ value: string; controller: AbortController } | null>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composing = useRef(false);
  const blurredDuringComposition = useRef(false);
  const mounted = useRef(true);

  function cancelPending() {
    pending.current?.controller.abort();
    pending.current = null;
    if (feedbackTimer.current !== null) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = null;
  }

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cancelPending(); };
  }, []);

  function change(next: string) {
    if (next === draft.current) return;
    draft.current = next;
    cancelPending();
    setValue(next);
    setStatus('idle');
  }

  async function save() {
    if (composing.current) { blurredDuringComposition.current = true; return; }
    const next = draft.current;
    if (next === saved.current || pending.current?.value === next) return;
    cancelPending();
    const job = { value: next, controller: new AbortController() };
    pending.current = job;
    setStatus('saving');
    try {
      await savePosterCopy(field, next, job.controller.signal);
      if (!mounted.current || pending.current !== job) return;
      saved.current = next;
      pending.current = null;
      setStatus('saved');
      feedbackTimer.current = setTimeout(() => {
        if (mounted.current && draft.current === next && saved.current === next) setStatus('idle');
        feedbackTimer.current = null;
      }, 2000);
    } catch {
      if (!mounted.current || pending.current !== job) return;
      pending.current = null;
      setStatus('failed');
    }
  }

  function compositionEnd(next: string) {
    change(next);
    composing.current = false;
    if (blurredDuringComposition.current) {
      blurredDuringComposition.current = false;
      void save();
    }
  }

  return { field, value, status, change, save, compositionStart: () => { composing.current = true; }, compositionEnd };
}
